import React from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  styled,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  Person,
  Inventory,
  LocalShipping,
  Store,
  Payment,
  Discount,
  Assignment,
  Info,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

import { BaseWidget } from 'modules/layer01';
import * as dataService from './dataService';
import type { IValidationErrors } from './dataService';

const ButtonBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between'
}));

export interface IProps {
  orderId: string;
}

const StatusIcon: React.FC<{ isComplete: boolean; isCritical?: boolean; }> = ({ isComplete, isCritical = false }) => {
  if (isComplete) {
    return <CheckCircle color="success" fontSize="small" />;
  }
  return isCritical ? <Error color="error" fontSize="small" /> : <Warning color="warning" fontSize="small" />;
};

const EligibilityCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  isComplete: boolean;
  missingReason?: string | null;
  missingItems?: any[];
  isCritical?: boolean;
  details?: React.ReactNode;
}> = ({ title, icon, isComplete, missingReason, missingItems, isCritical = false, details }) => {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              {icon}
              <Typography variant="subtitle1" fontWeight="medium">
                {title}
              </Typography>
            </Box>
            <StatusIcon isComplete={isComplete} isCritical={isCritical} />
          </Box>

          <Divider />

          {/* Status */}
          {isComplete ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircle color="success" fontSize="small" />
              <Typography variant="body2" color="success.main" fontWeight="medium">
                Ready
              </Typography>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              <Error color="error" fontSize="small" />
              <Typography variant="body2" color="error.main" fontWeight="medium">
                Issue Found
              </Typography>
              {missingReason && (
                <Typography variant="body2" color="text.secondary">
                  • {missingReason}
                </Typography>
              )}
            </Box>
          )}

          {/* Details */}
          {details && (
            <Box>
              {details}
            </Box>
          )}

          {/* Missing Items List */}
          {missingItems && missingItems.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" fontWeight="medium" mb={1}>
                Missing Items:
              </Typography>
              <List dense sx={{ py: 0 }}>
                {missingItems.map((item, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <Error color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          {item.sku || item.discountType}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {item.name || `Rate: ${item.rate}`}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const ValidationErrorsCard: React.FC<{
  validationErrors: IValidationErrors;
}> = ({ validationErrors }) => {
  const [expanded, setExpanded] = React.useState(true);

  const totalErrors = (validationErrors.orderWriteRequestErrors?.errors.length || 0) +
    (validationErrors.customerWriteRequestErrors?.errors.length || 0);

  if (totalErrors === 0) {
    return null;
  }

  return (
    <Card variant="outlined" sx={{ borderColor: 'error.main', borderWidth: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Error color="error" />
              <Typography variant="subtitle1" fontWeight="medium" color="error.main">
                Validation Errors
              </Typography>
              <Chip
                label={`${totalErrors} error${totalErrors !== 1 ? 's' : ''}`}
                color="error"
                size="small"
                sx={{ ml: 1 }}
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ p: 0.5 }}
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Divider />

          <Collapse in={expanded}>
            <Stack spacing={2}>
              {/* Order Write Request Errors */}
              {validationErrors.orderWriteRequestErrors && (
                <Box>
                  <Typography variant="body2" fontWeight="medium" color="text.secondary" mb={1}>
                    Order Write Request:
                  </Typography>
                  <List dense sx={{ py: 0, pl: 1 }}>
                    {validationErrors.orderWriteRequestErrors.errors.map((error, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <Error color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" color="error.main">
                              {error}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Customer Write Request Errors */}
              {validationErrors.customerWriteRequestErrors && (
                <Box>
                  <Typography variant="body2" fontWeight="medium" color="text.secondary" mb={1}>
                    Customer Write Request:
                  </Typography>
                  <List dense sx={{ py: 0, pl: 1 }}>
                    {validationErrors.customerWriteRequestErrors.errors.map((error, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <Error color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" color="error.main">
                              {error}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Stack>
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.orderId);
  const [syncOrderToNetSuite, syncMutationTuple] = dataService.useSyncOrderToNetSuite(props.orderId);

  if (queryTuple.loading) {
    return (
      <BaseWidget.FC title="NetSuite Sync Eligibility">
        <Box display="flex" justifyContent="center" p={3}>
          <Typography>Loading eligibility information...</Typography>
        </Box>
      </BaseWidget.FC>
    );
  }

  if (!queryTuple.data.eligibilityDetails) {
    return (
      <BaseWidget.FC title="NetSuite Sync Eligibility">
        <Alert severity="info" icon={<Info />}>
          This order is not eligible for NetSuite sync or eligibility data is not available.
        </Alert>
      </BaseWidget.FC>
    );
  }

  const data = queryTuple.data.eligibilityDetails;
  const overallComplete = data.customerInNetSuite &&
    data.allProductsInNetSuite &&
    data.hasShippingService &&
    data.storeHasDepartment &&
    data.storeHasLocation &&
    data.storeHasTaxItem &&
    data.hasMappedPaymentMethod &&
    data.discountItemsSynced;

  const criticalIssues = [
    !data.customerInNetSuite,
    !data.allProductsInNetSuite,
    !data.hasShippingService,
    !data.storeHasDepartment || !data.storeHasLocation || !data.storeHasTaxItem,
    !data.hasMappedPaymentMethod
  ].filter(Boolean).length;

  const handleSyncToNetSuite = () => {
    syncOrderToNetSuite();
  };

  const isSyncing = syncMutationTuple.loading;

  return (
    <BaseWidget.FC
      title="NetSuite Sync Eligibility"
      elAction={!data.orderInNetSuite && (
        <ButtonBox>
          <Button
            variant="contained"
            color="primary"
            disabled={isSyncing}
            onClick={handleSyncToNetSuite}
            startIcon={isSyncing ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {isSyncing ? 'Syncing...' : 'Sync to NetSuite'}
          </Button>
        </ButtonBox>
      )}>
      <Stack spacing={3}>
        {/* Overall Status Header */}
        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontWeight="bold" mb={0.5}>
                Sync Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {overallComplete ? 'Ready to sync to NetSuite' : `${criticalIssues} critical issues need resolution`}
              </Typography>
            </Box>
            <Chip
              label={overallComplete ? "Ready for Sync" : "Issues Found"}
              color={overallComplete ? "success" : "warning"}
              variant="filled"
              size="medium"
            />
          </Stack>
        </Paper>

        {/* Status Alerts */}
        {data.orderInNetSuite && (
          <Alert severity="info" icon={<Info />}>
            This order is already synced to NetSuite.
          </Alert>
        )}

        {data.writeRequestGenerated && !data.orderInNetSuite && (
          <Alert severity="info" icon={<Assignment />}>
            Write request has been generated for this order.
          </Alert>
        )}

        {/* Eligibility Cards Grid - Only show if order is not in NetSuite */}
        {!data.orderInNetSuite && (
          <Grid container spacing={2} sx={{ paddingRight: 4 }}>
            <Grid item xs={12} md={6}>
              <EligibilityCard
                title="Customer"
                icon={<Person color="primary" />}
                isComplete={data.customerInNetSuite}
                missingReason={data.customerMissingReason}
                isCritical={true}
                details={
                  data.customerEmail && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Email:</strong> {data.customerEmail}
                      </Typography>
                    </Box>
                  )
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <EligibilityCard
                title="Products & SKUs"
                icon={<Inventory color="primary" />}
                isComplete={data.allProductsInNetSuite}
                missingReason={data.missingProducts.length > 0 ? `${data.missingProducts.length} products missing from NetSuite` : undefined}
                missingItems={data.missingProducts}
                isCritical={true}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <EligibilityCard
                title="Shipping Service"
                icon={<LocalShipping color="primary" />}
                isComplete={data.hasShippingService}
                missingReason={data.shippingServiceMissingReason}
                isCritical={true}
                details={
                  data.requestedShippingService && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Requested:</strong> {data.requestedShippingService}
                      </Typography>
                    </Box>
                  )
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <EligibilityCard
                title="Store Configuration"
                icon={<Store color="primary" />}
                isComplete={data.storeHasDepartment && data.storeHasLocation && data.storeHasTaxItem}
                missingReason={data.storeMissingReason}
                isCritical={true}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <EligibilityCard
                title="Payment Method"
                icon={<Payment color="primary" />}
                isComplete={data.hasMappedPaymentMethod}
                missingReason={data.paymentMethodMissingReason}
                isCritical={true}
                details={
                  data.rawPaymentMethod && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Method:</strong> {data.rawPaymentMethod}
                      </Typography>
                    </Box>
                  )
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <EligibilityCard
                title="Discount Items"
                icon={<Discount color="primary" />}
                isComplete={data.discountItemsSynced}
                missingReason={data.missingDiscountItems.length > 0 ? `${data.missingDiscountItems.length} discount items missing from NetSuite` : undefined}
                missingItems={data.missingDiscountItems}
              />
            </Grid>
          </Grid>
        )}

        {/* Write Request Status - Only show if order is not in NetSuite */}
        {!data.orderInNetSuite && (
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
            <EligibilityCard
              title="Write Request Status"
              icon={<Assignment color="primary" />}
              isComplete={data.writeRequestGenerated}
              missingReason={!data.writeRequestGenerated ? "Write request has not been generated yet" : undefined}
            />
          </Paper>
        )}

        {/* Validation Errors - Only show if order is not in NetSuite and errors exist */}
        {!data.orderInNetSuite && queryTuple.data.validationErrors && (
          <ValidationErrorsCard validationErrors={queryTuple.data.validationErrors} />
        )}
      </Stack>
    </BaseWidget.FC>
  );
};
